import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { notificationAPI } from '../services/api';
import { useAuth } from './AuthContext';

const NotificationContext = createContext();

const POLL_INTERVAL_MS = 60000;

export const NotificationProvider = ({ children }) => {
    const { isAuthenticated } = useAuth();
    const [unreadCount, setUnreadCount] = useState(0);
    const [recentNotifications, setRecentNotifications] = useState([]);
    // Notifications currently shown as bottom-right popups.
    const [toasts, setToasts] = useState([]);
    // IDs already accounted for — either already popped up, or seen during
    // the very first check this session (so existing unread notifications
    // from before the page loaded don't all pop up at once on login).
    const seenIdsRef = useRef(new Set());
    const initializedRef = useRef(false);

    const refreshUnreadCount = useCallback(async () => {
        try {
            const res = await notificationAPI.getUnreadCount();
            if (res.success) {
                setUnreadCount(res.data.count);
            }
        } catch (error) {
            console.error('Error fetching unread notification count:', error);
        }
    }, []);

    const fetchRecent = useCallback(async () => {
        try {
            const res = await notificationAPI.getRecent();
            if (res.success) {
                setRecentNotifications(res.data);
            }
        } catch (error) {
            console.error('Error fetching recent notifications:', error);
        }
    }, []);

    const markAsRead = useCallback(async (id) => {
        setRecentNotifications(prev =>
            prev.map(n => (n.id === id ? { ...n, is_read: true } : n))
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
        try {
            await notificationAPI.markAsRead(id);
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    }, []);

    const markAllAsRead = useCallback(async () => {
        setRecentNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
        setUnreadCount(0);
        try {
            await notificationAPI.markAllAsRead();
        } catch (error) {
            console.error('Error marking all notifications as read:', error);
        }
    }, []);

    const dismissToast = useCallback((id) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    // Piggybacks on the same poll as the unread count — fetches the recent
    // list and pops up a toast for anything unread that wasn't there last
    // time. The first check after login/page load just seeds "already seen"
    // without popping anything, so your existing unread notifications don't
    // all fire at once the moment you open the site.
    const checkForNewNotifications = useCallback(async () => {
        try {
            const res = await notificationAPI.getRecent();
            if (!res.success) return;

            if (!initializedRef.current) {
                res.data.forEach(n => seenIdsRef.current.add(n.id));
                initializedRef.current = true;
                return;
            }

            const newOnes = res.data.filter(n => !n.is_read && !seenIdsRef.current.has(n.id));
            res.data.forEach(n => seenIdsRef.current.add(n.id));

            if (newOnes.length > 0) {
                setToasts(prev => [...prev, ...newOnes]);
            }
        } catch (error) {
            console.error('Error checking for new notifications:', error);
        }
    }, []);

    useEffect(() => {
        if (!isAuthenticated) {
            setUnreadCount(0);
            setRecentNotifications([]);
            setToasts([]);
            seenIdsRef.current = new Set();
            initializedRef.current = false;
            return;
        }

        refreshUnreadCount();
        checkForNewNotifications();
        const interval = setInterval(() => {
            refreshUnreadCount();
            checkForNewNotifications();
        }, POLL_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [isAuthenticated, refreshUnreadCount, checkForNewNotifications]);

    const value = {
        unreadCount,
        recentNotifications,
        toasts,
        dismissToast,
        fetchRecent,
        markAsRead,
        markAllAsRead,
        refreshUnreadCount,
    };

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
};

export const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotifications must be used within a NotificationProvider');
    }
    return context;
};
